'use strict';
/* ==========================================================================
   levels/mic.js — step "mic" (any): OPEN MIC II, the spoken-word piece "Who's Telling This?" performed by Nia on the
   school's open-mic stage, with zine karaoke (DESIGN.md §2, §5.4; content/mic.json). Built from
   lessons/simile/levels/mic.js (lesson-local copy, adapted: clue tags and POV eyes instead of device chips).

   ► The piece: assets/music/mic.m4a = ACE-Step take spoken_alt_21 (spoken word over a lo-fi beat, 80 s;
     assets/ASSETS.md, CHOICE.md). Timings: assets/music/mic_words.json (tools/song_timing.py: 24 lines, none
     interpolated, every line of the sheet performed): the TRUE lines of content/mic.json with word times from the
     take's Whisper passes, and the beat grid {bpm 83, offset 0.175 s}.

   The whole frame is a pure function of SONG TIME t (the shared <audio> element's clock while it plays; a wall clock
   while audio is locked or missing), so seeking, pausing and the QA screenshots are exact. Every picture beat is keyed
   to a spoken word (S.W(line, word)). Every clue tag and verdict comes from the model (core-local/narration.js): the
   verse-2 tags are the clues of the video's "My Volcano" passage (Narration.SCRIPT.volcano), the outro's MISSING INFO
   is Bea's (SCRIPT.bea), the stamp is Narration.reliability(volcano); the POV eyes use Narration.POVS / colour.

   The stage (always on): a dark club with red curtains, the stage boards and their light (Zine.stage), the spotlight
   lamp on its tripod at stage left, Nia at a desk mic on a tall table (she bobs while she speaks, points at the board
   on the key words), Ms. Park hosting at the right (pointing at the end), finger-snap doodles on the backbeat.
   A photocopied page taped up behind them (the board) shows what each line does, keyed to its words:
     intro    0        OPEN MIC II card; "Who's telling this?" types on; the case file and the typewriter
     page     1–2      "the page could never lie" typed; an open book; "in writing = true?"
     poe      3–4      "I blamed Poe": POE struck out in red; two index cards AUTHOR (writes the words) / NARRATOR (tells it)
     chorus   5–8, 17–20   the hook as big cut-out (ransom) letters: WHO'S TELLING THIS? / ASK BEFORE YOU TRUST, then
                       THE AUTHOR WROTE THE WORDS / BUT THE NARRATOR TALKS TO US
     volcano  9–12     the case board: MY VOLCANO card, the volcano + vinegar bottle, Pepper asleep; the clue tags
                       CONTRADICTS ("asleep upstairs"), OTHERS REACT ("Mom just stares"), SOMETHING TO GAIN ("an
                       extension") flicker on with red string to the claim's pin; the UNRELIABLE stamp on "blame"
     eyes     13–15    the POV cameras: one eye "I" (FIRST-PERSON LIMITED), one eye "he / she" (THIRD-PERSON LIMITED),
                       many eyes (THIRD-PERSON OMNISCIENT), each opening (and blinking) on its words
     persp    16       POINT OF VIEW = where you stand (a pin) / PERSPECTIVE = the feeling (a heart)
     outro    21–23    "Not every narrator lies": some lie (Marcus) / MISSING INFO: some just miss the facts (Bea); the
                       magnifier on "clues"; then the title card and FACT-CHECKED (+ snaps)
   Karaoke (bottom band, a photocopied strip): the current line in typewriter type, each word STAMPS on as it is spoken
   (unspoken words are faint), the spoken word in red; each clue / POV highlighter swipes under its words as they're said
   (MARKS below, the model's colours); "Poe" in "I blamed Poe" is struck out; the chorus lines stamp on as cut-out
   letters; the tag of the line flickers on at the band's right; the next line faint below.
   Controls: click / Space play-pause, ↺ restart (R), ⌂ map, ← → seek 5 s, click or drag the strip at the bottom;
   "Finish" once ≥ 90 % has been heard. The piece's end (≥ 90 % heard) → Game.complete('mic').
   Portrait phone: the stage on top, Dock.karaoke (chorus lines styled as the hook) + the tag note + ↺ ▶ ⌂ + the strip.
   Long audio (docs/WEBSITE.md §8.4): lesson.json preload.stream names the .m4a, so it plays through the shared <audio>
   element (Audio.media), never decoded into memory.

   QA hooks: sc.song (S(i), E(i), W(i, word), beats), sc.plan (acts, tStamp), sc.debugAt(t), sc.now(), sc.play(t),
   sc.pause(), sc.toggle(), sc.seek(t), sc.restart(), sc.done() / sc.finishStub(), sc.canFinish(), sc.lyricAt(t),
   sc.activeWord(t), sc.actAt(t), sc.marksAt(t) (the highlighter spans showing in the karaoke at t), sc.tagsAt(t) (the
   clue tags / eyes on the board at t), sc.heard, sc.ended, sc.audioReady; Levels.mic.MARKS, Levels.mic.isHook(text),
   Levels.mic.clueOf(src).
   ========================================================================== */
(function () {
  const STEP = 'mic';
  const TAKE_NAME = 'spoken_alt_21';
  const AUDIO = 'music/mic.m4a', WORDS = 'music/mic_words.json';
  const KEY = 'song:mic';
  const TYPE = "'Special Elite'";
  const Z = () => window.Zine;
  const ZC = () => (window.Zine ? Zine.C : { ink: '#141210', paper: '#f7f5ef', red: '#d9352c', grey: '#6d6a64', white: '#fffefa' });
  const N = () => window.Narration;
  const COL = id => (window.Narration ? Narration.colour(id) : '#fff04a');
  const PARK = '#2b4a8c';
  const BAND_Y = 832, CUR_Y = 916, NEXT_Y = 1012, CUR_SIZE = 54, NEXT_SIZE = 34;
  const FADE = 0.35;                                  // act cross-fade (s)
  const THRESH = 0.9;                                 // share of the piece heard for the stamp / "Finish"
  const START_DELAY = 0.7;                            // the mic tap, then the piece
  const CREDIT = 'AI-generated performance';
  const HOOK_PAL = [['#141210', '#f7f5ef'], ['#f7f5ef', '#141210'], ['#fff04a', '#141210'], ['news', '#141210'], ['#ff9d33', '#141210'], ['#141210', '#fff04a'], ['#52c8ff', '#141210']];

  /* the board (a photocopied page taped up behind the performer) and the cast */
  const BD = { x: 1185, y: 420, w: 820, h: 560 };
  const BL = BD.x - BD.w / 2 + 60, BW = BD.w - 120;   // the board's text column
  const NIA = { x: 560, y: 772, h: 600 }, TABLE = { x: 712, top: 478 }, PK = { x: 1792, y: 836, h: 560 };
  const STAGE = { x: 600, y: 770, w: 1000 };

  /* ---------------- the model's tags ---------------- */
  /* the clue of the model's passage sentence that contains `key` (Narration.SCRIPT[passage]): never a content field */
  function clueOf(src) {
    const M = N(); if (!M || !M.SCRIPT) return null;
    const [passage, key] = src, p = M.SCRIPT[passage];
    const s = p && p.sentences.find(x => x.text.includes(key));
    const ev = s && s.evidence ? [].concat(s.evidence) : [];
    return ev.length && M.CLUES[ev[0].clue] ? ev[0].clue : null;
  }
  /* the karaoke's marks (content/mic.json lines): a clue (id from the model's passage) or a POV (Narration.POVS);
     the words are found in the line */
  const MARKS = [
    { strike: true, phrase: 'Poe', inLine: 'I blamed Poe', at: 'book', note: 'Poe is the author: he wrote the words. The narrator tells the story.', label: 'NARRATOR, NOT AUTHOR' },
    { src: ['volcano', 'asleep upstairs'], phrase: 'asleep upstairs' },
    { src: ['volcano', 'Mom looked'], phrase: 'Mom just stares at him and frowns' },
    { src: ['volcano', 'extension'], phrase: 'wants an extension' },
    { pov: 'first-limited', phrase: 'first person, limited, one head' },
    { pov: 'third-limited', phrase: 'third-person limited' },
    { pov: 'third-omniscient', phrase: 'omniscient, all-knowing' },
    { src: ['bea', 'magic trick'], phrase: 'miss the facts' },
  ];
  /* the kind of unreliable narrator the model gives a passage (Narration.KINDS[why].short) */
  const kindShort = passage => { const M = N(), p = M && M.SCRIPT && M.SCRIPT[passage]; return p && M.KINDS[p.why] ? M.KINDS[p.why].short : ''; };
  const markId = m => m.pov || (m.src ? clueOf(m.src) : null);
  const normW = w => String(w).toLowerCase().replace(/[^a-z0-9]/g, '');
  const HOOK_TEXT = ["Who's telling this?", 'Ask before you trust', 'The author wrote the words', 'But the narrator talks to us'];
  const HOOKS = HOOK_TEXT.map(s => s.split(/\s+/).map(normW).join(' '));
  const isHook = text => HOOKS.includes(String(text).split(/\s+/).map(normW).filter(Boolean).join(' '));

  /* ---------------- song timing ---------------- */
  class Song {
    constructor(d) {
      this.d = d; this.lines = d.lines; this.dur = d.duration || 80;
      const b = d.beats || {};
      this.bpm = b.bpm || 84; this.bt = 60 / this.bpm; this.off = b.offset || 0;
      // per line: its tag spans, struck words, hook flag
      this.lines.forEach(l => {
        const ws = l.words.map(w => normW(w.w));
        l.marks = []; l.strike = []; l.hook = isHook(l.text);
        MARKS.forEach((m, mi) => {
          if (m.inLine && !l.text.includes(m.inLine)) return;
          const ph = m.phrase.split(/\s+/).map(normW);
          for (let k = 0; k + ph.length <= ws.length; k++) {
            if (!ph.every((p, j) => ws[k + j] === p)) continue;
            if (m.strike) { const a = m.at ? ws.indexOf(normW(m.at), k) : -1; l.strike.push({ j: k, mi, note: m.note, label: m.label, t: a >= 0 ? l.words[a].s : l.words[k].e }); }
            else { const id = markId(m); if (id) l.marks.push({ dev: id, j0: k, j1: k + ph.length - 1, mi }); }
            break;
          }
        });
      });
      this.items = d.lines.map(l => ({ ...l, kind: 'line' })).concat((d.extras || []).map((x, j) => ({ ...x, i: 100 + j, marks: [], strike: [], hook: false }))).sort((a, c) => a.s - c.s);
    }
    S(i) { return this.lines[i] ? this.lines[i].s : 1e6; }
    E(i) { return this.lines[i] ? this.lines[i].e : 1e6; }
    /* start of the n-th word of line i that matches `word` (exact, then prefix); the line start if none */
    W(i, word, n = 1) {
      const l = this.lines[i]; if (!l) return 1e6;
      const k = normW(word);
      for (const pass of [w => normW(w.w) === k, w => normW(w.w).startsWith(k)]) {
        let c = 0;
        for (const w of l.words) if (pass(w) && ++c === n) return w.s;
      }
      if (window.__songCueMiss) window.__songCueMiss.push(i + ':' + word);
      return l.s;
    }
    WE(i, word, n = 1) {                              // the end of that word
      const l = this.lines[i]; if (!l) return 1e6;
      const s = this.W(i, word, n), w = l.words.find(x => x.s === s);
      return w ? w.e : s + 0.3;
    }
    beatPos(t) { return (t - this.off) / this.bt; }
    sinceBeat(t) { const p = this.beatPos(t); return (p - Math.floor(p)) * this.bt; }
    pulse(t, k = 9) { return t < this.off - 0.05 ? 0 : Math.exp(-this.sinceBeat(t) * k); }
    speaking(t) { return this.lines.some(l => t >= l.s - 0.05 && t <= l.e + 0.1); }
  }

  /* ---------------- the plan: acts keyed to line starts ---------------- */
  function plan(S) {
    const b = i => S.S(i) - 0.3;
    const acts = [
      { id: 'intro', at: 0, draw: actIntro },
      { id: 'page', at: b(1), draw: actPage },
      { id: 'poe', at: b(3), draw: actPoe },
      { id: 'chorus1', at: b(5), draw: actChorus, c0: 5 },
      { id: 'volcano', at: b(9), draw: actVolcano },
      { id: 'eyes', at: b(13), draw: actEyes },
      { id: 'persp', at: S.S(16) - 0.1, draw: actPersp },
      { id: 'chorus2', at: b(17), draw: actChorus, c0: 17 },
      { id: 'outro', at: b(21), draw: actOutro },
    ];
    acts.forEach((a, i) => { a.end = i + 1 < acts.length ? acts[i + 1].at : S.dur + 1; });
    // the board steps aside for the chorus's cut-out letters
    const hooks = [[S.S(5) - 0.1, b(9)], [S.S(17) - 0.1, b(21)]];
    // the moments Nia points at the board (a clue or a point of view is named)
    const points = [S.W(3, 'Poe'), S.W(4, 'narrator'), S.W(10, 'asleep'), S.W(11, 'extension'), S.W(13, 'first'), S.W(14, 'third'), S.W(15, 'omniscient'), S.W(23, 'clues')];
    // the verdict of the video's volcano passage (the model), stamped on "blame"
    const V = N() && N().SCRIPT ? N().reliability(N().SCRIPT.volcano) : null;
    return { acts, ticks: acts.slice(1).map(a => a.at), hooks, points, verdict: V ? (V.reliable ? 'RELIABLE' : 'UNRELIABLE') : null, tStamp: S.E(23) + 0.9 };
  }
  function actAt(p, t) { let k = 0; for (let i = 0; i < p.acts.length; i++) if (t >= p.acts[i].at) k = i; return k; }
  function hookness(p, t) { let h = 0; for (const [a, b] of p.hooks) if (t >= a && t < b + FADE) h = Math.max(h, P(t, a, a + FADE) * (1 - P(t, b, b + FADE))); return h; }

  /* ---------------- drawing helpers ---------------- */
  const layoutCache = new Map();
  const fontOk = () => !document.fonts || document.fonts.status === 'loaded';
  function measure(str, font) {
    const k = font + '|' + str;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); g.font = font; w = g.measureText(str).width; g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  function ransomW(str, size, seed) {
    const k = 'R|' + str + '|' + size + '|' + seed;
    let w = layoutCache.get(k);
    if (w === undefined) { g.save(); w = ransom(str, -9999, -9999, size, 1e9, { seed, palette: HOOK_PAL }); g.restore(); if (fontOk()) layoutCache.set(k, w); }
    return w;
  }
  function withAlpha(a, fn) { if (a <= 0.001) return; g.save(); g.globalAlpha *= Math.min(1, a); try { fn(); } finally { g.restore(); } }
  /* words j0..j1 of line i typed on at their spoken times, wrapped into rows of maxW from (x, y); returns [{x0, x1, y}] */
  function typeWrap(S, t, i, x, y, size, o = {}) {
    const l = S.lines[i], out = []; if (!l) return out;
    const Zn = Z(), sp = Zn.measure(' ', size), maxW = o.maxW || BW, lh = o.lh || size * 1.35;
    const j0 = o.j0 || 0, j1 = Math.min(o.j1 == null ? 1e9 : o.j1, l.words.length - 1);
    let cx = x, cy = y;
    for (let j = j0; j <= j1; j++) {
      const w = l.words[j], txt = o.upper ? w.w.toUpperCase() : w.w, ww = Zn.measure(txt, size);
      if (cx > x && cx + ww > x + maxW) { cx = x; cy += lh; }
      out.push({ x0: cx, x1: cx + ww, y: cy, s: w.s, e: w.e, j });
      if (!o.dry && t >= w.s) Zn.type(txt, cx, cy, size, { t0: w.s, cps: Math.max(18, txt.length / Math.max(0.12, (w.e - w.s) * 0.8)), seed: (o.seed || 1) + j, color: o.color });
      cx += ww + sp;
    }
    return out;
  }
  /* a highlighter swipe under a run of words (split per row), drawn on from the first word's start to the last's end */
  function swipe(ws, size, colour, t, seed) {
    if (!ws.length || t < ws[0].s) return;
    const rows = [];
    ws.forEach(w => { const r = rows.find(q => q.y === w.y); if (r) r.w.push(w); else rows.push({ y: w.y, w: [w] }); });
    rows.forEach((r, k) => {
      const a = r.w[0], b = r.w[r.w.length - 1];
      if (t < a.s - 0.05) return;
      Z().highlight(a.x0 - 10, r.y + 2, b.x1 - a.x0 + 20, size * 1.1, colour, { prog: E.outQuad(P(t, a.s - 0.05, Math.max(a.s + 0.2, b.e))), seed: seed + k });
    });
  }
  /* a red ink strike through [x0, x1] at y */
  function strike(x0, x1, y, size, t, t0, seed) {
    if (t < t0) return;
    ink([[x0 - 8, y + size * 0.08], [x1 + 8, y - size * 0.12]], { w: Math.max(5, size * 0.1), color: ZC().red, seed, prog: E.outCubic(P(t, t0, t0 + 0.25)), passes: 2, step: 12, alpha: g.globalAlpha });
  }
  /* a neon tag that FLICKERS on (a sign catching: two quick blinks, then steady) */
  const flick = (t, t0) => { const u = t - t0; if (u < 0) return 0; if (u > 0.42) return 1; return [1, 0.15, 1, 0.3, 1, 1][Math.min(5, Math.floor(u / 0.07))]; };
  function tag(id, x, y, t, t0, o = {}) {
    if (t < t0) return 0;
    let w = 0;
    withAlpha(flick(t, t0), () => { w = Z().label(id, x, y, { size: o.size || 30, rot: o.rot || 0, s: pop(t, t0, 0.3, 2.2), seed: o.seed || 1, text: o.text || null }); });
    return w;
  }
  const tagW = (id, size, text) => { const tx = text || (N() ? N().name(id) : id).toUpperCase(); return Z().measure(tx, size) + size * 2.6; };
  /* an eye that opens on its word and blinks now and then (pure in t) */
  function eyeAt(x, y, r, t, t0, id, seed, lab) {
    if (t < t0) return;
    const bl = (t - t0) % 3.1, blink = bl > 2.85 ? Math.abs(Math.cos((bl - 2.85) / 0.25 * Math.PI)) : 1;
    const open = E.outBack(P(t, t0, t0 + 0.35), 1.6) * blink;
    withAlpha(flick(t, t0), () => Z().eye(x, y, r, { open: Math.max(0.05, open), fill: COL(id), seed, label: lab }));
  }

  /* ---------------- the stage ---------------- */
  const SNAPS = [[120, 400], [300, 250], [1650, 190], [1300, 760], [340, 560], [1555, 760], [90, 180], [990, 750]];
  function backdrop(S, p, t) {
    const Zn = Z();
    g.fillStyle = paperTile('#2c2724', 51, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
    const sp = Zn.speckle(960, 540, 52, 0.9); g.save(); g.globalCompositeOperation = 'screen'; g.globalAlpha = 0.22; g.drawImage(sp, 0, 0, 1920, 1080); g.restore();
    for (let k = 0; k < 3; k++) {
      paperRect(30 + k * 58, 470, 68, 1000, k % 2 ? '#6e1f1b' : '#842723', { seed: 601 + k, depth: 0.8, boil: false });
      paperRect(1890 - k * 58, 470, 68, 1000, k % 2 ? '#6e1f1b' : '#842723', { seed: 611 + k, depth: 0.8, boil: false });
    }
    Zn.stage(STAGE.x, STAGE.y, STAGE.w, { seed: 620 });
    // the spotlight lamp on its tripod at stage left (the cut-out aims up-right, at Nia) + its beam
    const pu = S.pulse(t, 5);
    g.save(); g.globalCompositeOperation = 'lighter';
    const gr = g.createLinearGradient(250, 560, NIA.x, 300);
    gr.addColorStop(0, `rgba(255,246,210,${0.2 + 0.04 * pu})`); gr.addColorStop(1, 'rgba(255,246,210,0.02)');
    g.fillStyle = gr; g.beginPath(); g.moveTo(236, 548); g.lineTo(NIA.x + 170, 150); g.lineTo(NIA.x + 120, 640); g.lineTo(262, 590); g.closePath(); g.fill();
    g.restore();
    Zn.art('spotlight', 200, STAGE.y + 6, 250, { seed: 621 });
  }
  function snaps(S, t) {
    const Zn = Z(), bp = S.beatPos(t), fl = Math.floor(bp);
    if (t < S.off || fl % 2 !== 1) return;                 // the backbeat (beats 2 and 4)
    const since = S.sinceBeat(t), k = Math.floor(hash1(fl * 7.13) * SNAPS.length), [x, y] = SNAPS[k];
    const s = pop(since, 0, 0.12, 2.2) * (1 - P(since, S.bt * 0.55, S.bt * 0.95));
    if (s <= 0.01) return;
    g.save(); g.translate(x, y); g.rotate((hash1(fl * 3.7) - 0.5) * 0.4); g.scale(s, s);
    Zn.doodle('onomatopoeia', 0, 0, 30, { color: '#f7f5ef', seed: 640 + (fl % 5), w: 3.5 });
    Zn.type('snap', 0, 48, 24, { align: 'center', color: '#f7f5ef', seed: 650 + (fl % 5) });
    g.restore();
  }
  function cast(S, p, t) {
    const speaking = S.speaking(t), pu = S.pulse(t, 7);
    const env = speaking ? 0.3 + 0.5 * pu : 0.12 * pu;
    const pointing = p.points.some(a => t >= a && t < a + 1.6);
    Puppet.draw('nia', NIA.x, NIA.y, NIA.h, { facing: 1, t, env, pose: pointing ? 'point' : 'nia' });
    // the tall table with the desk mic, in front of her
    paperRect(TABLE.x, (TABLE.top + STAGE.y) / 2 + 6, 26, STAGE.y - TABLE.top, '#3a3431', { seed: 661, depth: 0.8, boil: false });
    paperRect(TABLE.x, STAGE.y - 6, 120, 16, '#3a3431', { seed: 662, depth: 0.6, boil: false });
    paperRect(TABLE.x, TABLE.top, 170, 22, '#4a4440', { seed: 663, depth: 0.9, boil: false });
    Z().art('microphone_stand', TABLE.x + 6, TABLE.top - 8, 200, { seed: 664 });
    // Ms. Park hosting at the right; pointing when the piece is fact-checked
    const end = t >= p.tStamp;
    Puppet.draw('park', PK.x, PK.y, PK.h, { facing: -1, t, env: 0.08 * pu, pose: end ? 'point' : 'park' });
  }
  /* the board page (fades out while the chorus's letters are up) */
  function board(p, t) {
    const a = 1 - hookness(p, t);
    withAlpha(a, () => { Z().page(BD.x, BD.y, BD.w, BD.h, { seed: 671, rot: -0.008, tape: 2, dark: 0.45 }); });
    return a;
  }

  /* ======================================================================
     the acts (on the board)
     ====================================================================== */
  function actIntro(S, t) {
    const Zn = Z(), C = ZC();
    Zn.type('OPEN MIC II', BL, BD.y - 210, 44, { t0: -1e6, seed: 701 });
    Zn.type('tonight: Nia Brooks', BL + 330, BD.y - 208, 30, { t0: -1e6, color: C.grey, seed: 702 });
    typeWrap(S, t, 0, BL, BD.y - 110, 40, { j1: 2, seed: 710 });
    typeWrap(S, t, 0, BL, BD.y - 10, 60, { j0: 3, seed: 720, upper: true });
    Zn.art('case_file', BD.x - 170, BD.y + BD.h / 2 - 34, 170, { seed: 740, rot: -0.05 });
    Zn.art('typewriter', BD.x + 250, BD.y + BD.h / 2 - 30, 190, { seed: 741 });
  }

  /* "I used to think the page could never lie / If it's in writing, then it's true" */
  function actPage(S, t) {
    const Zn = Z(), C = ZC();
    typeWrap(S, t, 1, BL, BD.y - 200, 44, { seed: 800 });
    typeWrap(S, t, 2, BL, BD.y - 40, 40, { seed: 810 });
    const tb = S.W(1, 'page');
    if (t >= tb) Zn.doodle('allusion', BD.x + 250, BD.y + 170, 80, { t0: tb, dur: 0.5, seed: 820, w: 7 });
    const tw = S.W(2, 'why');
    if (t >= tw) hand('in writing = true?', BL + 20, BD.y + 170, 50, tw, { align: 'left', color: C.red, seed: 821, dur: 0.4 });
  }

  /* "I blamed Poe for the crime in the book / The narrator did it, Poe just wrote how it looked" */
  function actPoe(S, t) {
    const Zn = Z(), C = ZC();
    const ws = typeWrap(S, t, 3, BL, BD.y - 215, 38, { seed: 900 });
    const pw = ws.find(w => normW(S.lines[3].words[w.j].w) === 'poe');
    if (pw) strike(pw.x0, pw.x1, pw.y, 38, t, S.W(3, 'book'), 901);
    // the two index cards: AUTHOR (writes the words) / NARRATOR (tells the story) — N9
    const ta = S.W(3, 'Poe'), tn = S.W(4, 'narrator'), tw = S.W(4, 'wrote');
    const cy = BD.y + 90, cw = 330, ch = 300;
    if (t >= ta) {
      const s = pop(t, ta, 0.35, 1.8);
      Zn.card(BD.x - 190, cy, cw, ch, { seed: 910, rot: -0.03, s, title: 'AUTHOR', t0: ta, pin: false });
      if (s > 0.9) Zn.pin(BD.x - 190 + cw / 2 - 34, cy - ch / 2 + 26);
      if (s > 0.5) withAlpha(P(t, ta + 0.1, ta + 0.4), () => {
        Zn.type('Edgar Allan Poe', BD.x - 190 - cw / 2 + 30, cy - 30, 28, { t0: ta + 0.1, cps: 40, seed: 911 });
        if (t >= tw) hand('wrote the words', BD.x - 190, cy + 60, 42, tw, { color: C.red, seed: 912, dur: 0.35 });
      });
    }
    if (t >= tn) {
      const s = pop(t, tn, 0.35, 1.8);
      Zn.card(BD.x + 200, cy, cw, ch, { seed: 920, rot: 0.03, s, title: 'NARRATOR', t0: tn, pin: false });
      if (s > 0.9) Zn.pin(BD.x + 200 + cw / 2 - 34, cy - ch / 2 + 34);
      if (s > 0.5) withAlpha(P(t, tn + 0.1, tn + 0.4), () => {
        Zn.type('the "I" in the story', BD.x + 200 - cw / 2 + 30, cy - 30, 26, { t0: tn + 0.1, cps: 40, seed: 921 });
        hand('tells it', BD.x + 200, cy + 60, 46, tn + 0.3, { color: C.red, seed: 922, dur: 0.35 });
      });
    }
  }

  /* the chorus: the hook as big cut-out letters, two lines at a time */
  function actChorus(S, t, A) {
    const c0 = A.c0;
    hookLetters(S, t, c0, c0 + 1, 1 - P(t, S.S(c0 + 2) - 0.25, S.S(c0 + 2) - 0.05));
    hookLetters(S, t, c0 + 2, c0 + 3, 1 - P(t, A.end - 0.2, A.end + 0.1));
  }
  function rowsOf(words) {
    const n = words.length; let best = 1, bd = 1e9;
    for (let k = 1; k < n; k++) { const a = words.slice(0, k).join(' ').length, b = words.slice(k).join(' ').length; if (Math.abs(a - b) < bd) { bd = Math.abs(a - b); best = k; } }
    return [words.slice(0, best), words.slice(best)];
  }
  function hookLetters(S, t, la, lb, alpha = 1) {
    if (t < S.S(la) - 0.05 || alpha <= 0.001) return;
    const pu = S.pulse(t, 7);
    withAlpha(alpha, () => [la, lb].forEach((li, n) => {
      const l = S.lines[li]; if (!l || t < l.s - 0.05) return;
      const W = l.words.map(w => w.w.replace(/[,.!]+$/, '').toUpperCase());
      const rows = W.length <= 2 ? [W.map((w, j) => j)] : rowsOf(W.map((w, j) => j));
      const seedB = 1200 + li * 17;
      const fit = Math.min(92, ...rows.map(r => 100 * (BD.w + 60) / ransomW(r.map(j => W[j]).join(' '), 100, seedB)));
      rows.forEach((row, r) => {
        const y0 = (n ? 470 : 200) + r * fit * 1.3 + (rows.length === 1 ? fit * 0.65 : 0), gap = fit * 0.42;
        const wsz = row.map(j => ransomW(W[j], fit, seedB + j));
        const tot = wsz.reduce((a, c) => a + c, 0) + gap * (row.length - 1);
        let x = BD.x - tot / 2;
        row.forEach((j, q) => {
          const w = l.words[j], cx = x + wsz[q] / 2; x += wsz[q] + gap;
          if (t < w.s) return;
          const s = 1 + 0.04 * pu + 0.14 * Math.exp(-(t - w.s) * 7);
          g.save(); g.translate(cx, y0); g.scale(s, s);
          ransom(W[j], 0, 0, fit, w.s, { seed: seedB + j, stagger: 0.02, palette: HOOK_PAL });
          g.restore();
        });
      });
    }));
  }

  /* Verse 2, the case board: Marcus's claim, the props, the clue tags on their words, the verdict on "blame" */
  function actVolcano(S, t, A, p) {
    const Zn = Z(), C = ZC();
    typeWrap(S, t, 9, BL, BD.y - 225, 34, { seed: 1300, maxW: BW });
    // the claim's pin, where every string starts
    const PX = BD.x - 250, PY = BD.y - 20;
    const tv = S.W(9, 'volcano'), td = S.W(9, 'dog');
    if (t >= tv) {
      Zn.art('volcano', BD.x - 250, BD.y + 250, 210, { seed: 1301, s: pop(t, tv, 0.35, 1.8) });
      Zn.art('vinegar_bottle', BD.x - 90, BD.y + 250, 150, { seed: 1302, rot: 0.06, s: pop(t, tv + 0.25, 0.35, 1.8) });
    }
    if (t >= td) {
      Zn.art('dog', BD.x + 250, BD.y - 55, 105, { seed: 1303, s: pop(t, td, 0.35, 1.8) });
      if (t >= S.W(10, 'asleep')) hand('upstairs, asleep', BD.x + 250, BD.y - 20, 30, S.W(10, 'asleep'), { color: C.grey, seed: 1304, dur: 0.3 });
    }
    // the tags, each flickering on as its words are spoken (the clue ids are the model's), strung to the claim's pin
    const tg = [
      { m: 1, line: 10, word: 'asleep', y: BD.y + 60 },
      { m: 2, line: 10, word: 'frowns', y: BD.y + 135 },
      { m: 3, line: 11, word: 'extension', y: BD.y + 210 },
    ];
    tg.forEach((q, k) => {
      const id = markId(MARKS[q.m]), t0 = S.W(q.line, q.word);
      if (!id || t < t0) return;
      const w = tagW(id, 28), x = BD.x + BD.w / 2 - 50 - w / 2;
      Zn.string(PX, PY, x - w / 2 + 6, q.y, { t0: t0 + 0.1, dur: 0.45, sag: 30 });
      tag(id, x, q.y, t, t0, { size: 28, rot: (k - 1) * 0.02, seed: 1310 + k * 5 });
    });
    if (t >= S.W(9, 'Marcus')) Zn.pin(PX, PY, { r: 14 });
    // the verdict (Narration.reliability of the video's volcano passage) on "blame"
    const tb = S.W(12, 'blame');
    if (p.verdict && t >= tb) Zn.stamp(p.verdict, BD.x - 150, BD.y - 108, { rot: -0.1, s: pop(t, tb, 0.3, 2.2) * 0.62 });
  }

  /* the bridge: the POV cameras, one row per point of view, each eye opening on its words */
  function actEyes(S, t) {
    const Zn = Z(), C = ZC();
    const rows = [
      { id: 'first-limited', line: 13, word: 'first', y: BD.y - 170, eyes: 1, big: 'I', note: 'one head', nw: 'head' },
      { id: 'third-limited', line: 14, word: 'third', y: BD.y - 5, eyes: 1, big: 'he / she', note: 'one head', nw: 'head' },
      { id: 'third-omniscient', line: 15, word: 'omniscient', y: BD.y + 160, eyes: 4, big: '', note: 'many heads', nw: 'many' },
    ];
    rows.forEach((r, k) => {
      const tp = S.W(r.line, r.big === 'I' ? 'I' : r.big ? 'He' : 'Inside'), tl = S.W(r.line, r.word), tn = S.W(r.line, r.nw);
      if (t < Math.min(tp, tl)) return;
      if (r.eyes === 1) eyeAt(BL + 50, r.y, 44, t, tp, r.id, 1400 + k * 10);
      else for (let e = 0; e < r.eyes; e++) eyeAt(BL + 10 + e * 46, r.y + (e % 2 ? 16 : -16), 24, t, tp + e * 0.12, r.id, 1400 + k * 10 + e);
      if (r.big) Zn.type(r.big, BL + (r.eyes === 1 ? 110 : 200), r.y - 34, 40, { t0: tp, cps: 20, seed: 1450 + k });
      const w = tagW(r.id, 28), x = BL + 190 + w / 2;
      tag(r.id, Math.min(x, BD.x + BD.w / 2 - 40 - w / 2), r.y + 26, t, tl, { size: 28, seed: 1460 + k * 5 });
      if (t >= tn) hand(r.note, BD.x + BD.w / 2 - 110, r.y - 36, 34, tn, { color: C.red, seed: 1470 + k, dur: 0.3 });
    });
  }

  /* "Point of view is where you stand, perspective is the feeling it's showing" (N5, N6; Correction 3) */
  function actPersp(S, t) {
    const Zn = Z(), C = ZC();
    const tp = S.W(16, 'Point'), ts = S.W(16, 'stand'), tq = S.W(16, 'perspective'), tf = S.W(16, 'feeling');
    if (t >= tp) Zn.type('POINT OF VIEW', BL, BD.y - 170, 48, { t0: tp, cps: 30, seed: 1500 });
    if (t >= ts) {
      hand('= where you stand', BL + 40, BD.y - 80, 46, ts, { align: 'left', color: C.red, seed: 1501, dur: 0.4 });
      Zn.pin(BD.x + 280, BD.y - 130, { r: 22 });
      Zn.doodle('eye', BD.x + 280, BD.y - 60, 34, { t0: ts, dur: 0.4, seed: 1502, w: 5 });
    }
    if (t >= tq) Zn.type('PERSPECTIVE', BL, BD.y + 50, 48, { t0: tq, cps: 30, seed: 1503 });
    if (t >= tf) {
      hand('= the feeling it shows', BL + 40, BD.y + 140, 46, tf, { align: 'left', color: C.red, seed: 1504, dur: 0.4 });
      Zn.doodle('big-feelings', BD.x + 280, BD.y + 80, 50, { t0: tf, dur: 0.45, seed: 1505, w: 6, color: C.red });
    }
  }

  function actOutro(S, t, A, p) {
    const tS = p.tStamp, Zn = Z(), C = ZC(), tEnd = S.E(23) + 0.2;
    withAlpha(1 - P(t, tEnd, tEnd + 0.3), () => {
      typeWrap(S, t, 21, BL, BD.y - 215, 44, { seed: 1600 });
      const tl = S.W(21, 'lies');
      if (t >= tl) hand(`some are ${kindShort('volcano')} (Marcus)`, BL + 10, BD.y - 130, 36, tl + 0.1, { align: 'left', color: C.grey, seed: 1601, dur: 0.4 });
      const tm = S.W(22, 'miss'), id = markId(MARKS[7]);
      if (id && t >= tm) {
        const w = tagW(id, 30);
        tag(id, BL + w / 2, BD.y - 45, t, tm, { size: 30, seed: 1602 });
        hand(`some are ${kindShort('bea')} (Bea)`, BL + 10, BD.y + 30, 36, S.W(22, 'facts'), { align: 'left', color: C.grey, seed: 1603, dur: 0.4 });
      }
      const tc = S.W(23, 'clues');
      if (t >= tc) {
        Zn.art('magnifier', BD.x + 250, BD.y + 250, 190, { seed: 1604, rot: 0.2, s: pop(t, tc, 0.35, 1.8) });
        Zn.type('read the clues', BL, BD.y + 130, 40, { t0: tc, cps: 34, seed: 1605 });
      }
    });
    // the board comes back as Nia's zine page: the title, her byline, then FACT-CHECKED
    if (t >= tEnd) withAlpha(P(t, tEnd, tEnd + 0.4), () => {
      const sz = Math.min(80, 100 * (BD.w - 120) / ransomW("WHO'S TELLING", 100, 1702));
      ransom("WHO'S TELLING", BD.x, BD.y - 150, sz, tEnd + 0.1, { seed: 1701, palette: HOOK_PAL });
      ransom('THIS?', BD.x, BD.y - 150 + sz * 1.3, sz, tEnd + 0.25, { seed: 1702, palette: HOOK_PAL });
      Zn.type('a spoken-word piece by Nia Brooks', BD.x, BD.y + 225, 34, { t0: tEnd + 0.3, cps: 60, align: 'center', color: C.grey, seed: 1703 });
    });
    if (t >= tS) {
      SNAPS.forEach(([x, y], i) => {
        const a = tS + i * 0.1, s = pop(t, a, 0.25, 2.2) * (1 - P(t, a + 1.6, a + 2.0));
        if (s <= 0.01) return;
        g.save(); g.translate(x, y); g.rotate((i % 3 - 1) * 0.15); g.scale(s, s);
        Zn.doodle('onomatopoeia', 0, 0, 36, { color: '#f7f5ef', seed: 1610 + i, w: 4 });
        Zn.type('snap!', 0, 56, 28, { align: 'center', color: '#f7f5ef', seed: 1620 + i });
        g.restore();
      });
      Zn.stamp((Game.lesson && Game.lesson.completeCard && Game.lesson.completeCard.title) || 'FACT-CHECKED', BD.x + 60, BD.y + 80, { rot: -0.12, s: pop(t, tS, 0.3, 2.2) * 0.95 });
    }
  }

  function drawStage(S, p, t) {
    backdrop(S, p, t);
    board(p, t);
    const k = actAt(p, t), A = p.acts[k];
    const run = (i, a) => withAlpha(a, () => { g.save(); const X = p.acts[i]; if (X.id.startsWith('chorus')) X.draw(S, t, X, p); else withAlpha(1 - hookness(p, t), () => X.draw(S, t, X, p)); g.restore(); });
    if (k > 0 && t < A.at + FADE) run(k - 1, 1 - P(t, A.at, A.at + FADE));
    run(k, k === 0 ? 1 : P(t, A.at, A.at + FADE * 0.6));
    cast(S, p, t);
    snaps(S, t);
  }
  /* the tags / eyes the board shows at t (QA): [{act, id, line, word}] */
  function tagsAt(S, p, t) {
    const act = p.acts[actAt(p, t)].id, out = [];
    if (act === 'volcano') [[1, 10, 'asleep'], [2, 10, 'frowns'], [3, 11, 'extension']].forEach(([m, l, w]) => { if (t >= S.W(l, w)) out.push({ act, id: markId(MARKS[m]), line: l, word: w }); });
    if (act === 'eyes') [['first-limited', 13, 'first'], ['third-limited', 14, 'third'], ['third-omniscient', 15, 'omniscient']].forEach(([id, l, w]) => { if (t >= S.W(l, w)) out.push({ act, id, line: l, word: w }); });
    if (act === 'outro' && t >= S.W(22, 'miss') && t < S.E(23) + 0.5) out.push({ act, id: markId(MARKS[7]), line: 22, word: 'miss' });
    return out;
  }

  /* ---------------- karaoke ---------------- */
  const lays = new Map();
  function layout(item, size) {
    const key = item.i + '|' + item.s + '|' + size;
    if (lays.has(key)) return lays.get(key);
    const f = `${size}px ${TYPE}`, space = measure(' ', f) + size * 0.1, rs = size * 0.78;
    let x = 0;
    const toks = item.words.map((w, j) => {
      const tk = { text: w.w, s: w.s, e: w.e, j, seed: 400 + item.i * 13 + j * 5 };
      if (item.hook) { tk.up = w.w.replace(/[,.!?:]+$/, '').toUpperCase(); tk.trail = (w.w.match(/[,.!?:]+$/) || [''])[0]; tk.w = ransomW(tk.up, rs, tk.seed); tk.x = x; x += tk.w + (tk.trail ? measure(tk.trail, f) : 0) + space + size * 0.15; }
      else { tk.w = measure(w.w, f); tk.x = x; x += tk.w + space; }
      return tk;
    });
    const out = { toks, width: x - space, size, rs, f };
    if (fontOk()) lays.set(key, out);
    return out;
  }
  const tokEnd = (toks, j) => { const tk = toks[j], nx = toks[j + 1]; return Math.min(Math.max(tk.e, tk.s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); };
  function drawCurrent(item, t, y, maxW = 1560) {
    let size = CUR_SIZE, Lo = layout(item, size);
    if (Lo.width > maxW) { size = Math.floor(size * maxW / Lo.width); Lo = layout(item, size); }
    const x0 = 900 - Lo.width / 2, C = ZC(), Zn = Z();
    const appear = item.appear ?? item.s - 0.5, a = E.outCubic(P(t, appear, appear + 0.25)), yy = y + (1 - a) * 30;
    // the tag's highlighter under its words, drawn on as they're said
    item.marks.forEach((m, q) => {
      const A = Lo.toks[m.j0], B = Lo.toks[m.j1];
      if (t < A.s - 0.05) return;
      Zn.highlight(x0 + A.x - 10, yy + size * 0.04, B.x + B.w - A.x + 20, size * 1.12, COL(m.dev), { prog: E.outQuad(P(t, A.s - 0.05, Math.max(A.s + 0.2, B.e))), seed: 90 + item.i * 3 + q });
    });
    Lo.toks.forEach((tk, j) => {
      const x = x0 + tk.x, sung = t >= tk.s, active = t >= tk.s - 0.02 && t < tokEnd(Lo.toks, j);
      if (item.hook) {
        if (!sung) withAlpha(0.22 * a, () => { g.font = `${Math.round(size * 0.9)}px ${TYPE}`; g.fillStyle = C.ink; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(tk.up, x + tk.w / 2, yy); });
        else {
          const beat = 1 + 0.08 * Math.exp(-(t - tk.s) * 6);
          g.save(); g.translate(x + tk.w / 2, yy); g.scale(beat, beat);
          ransom(tk.up, 0, 0, Lo.rs, tk.s, { seed: tk.seed, stagger: clamp((tk.e - tk.s) / Math.max(1, tk.up.length), 0.015, 0.05), palette: HOOK_PAL });
          g.restore();
          if (tk.trail) { g.save(); g.font = Lo.f; g.fillStyle = C.ink; g.textAlign = 'left'; g.textBaseline = 'middle'; g.fillText(tk.trail, x + tk.w + 4, yy); g.restore(); }
        }
        return;
      }
      g.save(); g.font = Lo.f; g.textAlign = 'center'; g.textBaseline = 'middle';
      const cx = x + tk.w / 2;
      if (!sung) { g.globalAlpha *= 0.22 * a; g.fillStyle = C.ink; g.fillText(tk.text, cx, yy); }
      else {
        // the word STAMPS on: big and light → its size, full ink (a typewriter strike)
        const k = Math.exp(-(t - tk.s) * 16), s = 1 + 0.35 * k;
        g.translate(cx, yy + size * 0.04 * hash1(tk.seed)); g.rotate((hash1(tk.seed + 1) - 0.5) * 0.04); g.scale(s, s);
        g.globalAlpha *= 1 - 0.5 * k;
        g.fillStyle = active ? C.red : C.ink; g.fillText(tk.text, 0, 0);
      }
      g.restore();
      const st = item.strike.find(q => q.j === j);
      if (st && t >= st.t) strike(x, x + tk.w, yy, size, t, st.t, 95 + j);
    });
    return size;
  }
  /* a line comes up 0.5 s before its first word, but never before the previous line has been spoken */
  const appearAt = (it, k) => (k === 0 ? it[0].s - 0.5 : Math.min(it[k].s, Math.max(it[k].s - 0.5, it[k - 1].e + 0.05)));
  function lyricAt(song, t) {
    const it = song.items;
    let cur = -1;
    for (let k = 0; k < it.length; k++) if (t >= appearAt(it, k)) cur = k;
    let c = cur >= 0 ? it[cur] : null;
    if (c) c.appear = appearAt(it, cur);
    const nx = it[cur + 1] || null;
    if (c && t > c.e + 2.2 && (!nx || nx.s - t > 1.2)) c = null;
    return { cur: c, next: nx && nx.s - t < 8 ? nx : null };
  }
  function activeWord(song, t) {
    const { cur } = lyricAt(song, t); if (!cur) return null;
    const ws = cur.words;
    for (let j = 0; j < ws.length; j++) { const nx = ws[j + 1], end = Math.min(Math.max(ws[j].e, ws[j].s + 0.18) + 0.05, nx ? nx.s - 0.02 : 1e9); if (t >= ws[j].s - 0.02 && t < end) return { line: cur.i, j, w: ws[j].w, s: ws[j].s, e: ws[j].e }; }
    return null;
  }
  /* the tag the current line shows (for the band's chip / the dock note), once its first word is said */
  function tagNote(cur, t) {
    if (!cur) return null;
    const ms = cur.marks.filter(x => t >= cur.words[x.j0].s - 0.05);
    const m = ms[ms.length - 1];
    if (m) return { dev: m.dev, at: cur.words[m.j0].s, text: `${N().name(m.dev)}: "${cur.words.slice(m.j0, m.j1 + 1).map(w => w.w).join(' ').replace(/[,.!?]+$/, '')}"` };
    const st = cur.strike.find(x => t >= x.t);
    if (st) return { dev: 'none', at: st.t, text: st.note, label: st.label };
    return null;
  }
  function band(song, t) {
    const C = ZC(), Zn = Z();
    Zn.page(960, BAND_Y + 140, 2040, 290, { seed: 77, dark: 0.3 });
    tape(110, BAND_Y + 6, 150, -0.08, undefined, 71); tape(1810, BAND_Y + 6, 150, 0.08, undefined, 72);
    const { cur, next } = lyricAt(song, t);
    const sec = (cur || next || {}).section;
    if (sec) Zn.type(sec.toLowerCase(), 70, BAND_Y + 36, 26, { color: C.grey, seed: 73 });
    const dn = tagNote(cur, t);
    if (dn) {
      const lw = tagW(dn.dev, 26, dn.label);
      withAlpha(flick(t, dn.at), () => Zn.label(dn.dev, 1880 - lw / 2, BAND_Y + 26, { size: 26, rot: 0.02, s: pop(t, dn.at, 0.3, 2), seed: 74, text: dn.label || null }));
    }
    if (cur) drawCurrent(cur, t, CUR_Y);
    const note = (txt, col, a) => { let size = NEXT_SIZE; const f = s => `${s}px ${TYPE}`; const w = measure(txt, f(size)); if (w > 1700) size = Math.floor(size * 1700 / w);
      g.save(); g.globalAlpha *= a; g.font = f(size); g.fillStyle = col; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillText(txt, 960, NEXT_Y); g.restore(); };
    if (next) note(next.text, C.grey, 0.8);
    else if (song.items.length && t > song.items[song.items.length - 1].e) note('Who\'s telling this? Ask before you trust.', PARK, 0.85);
    if (!cur && !next && t > 1) {
      const b = ((Math.floor(song.beatPos(t)) % 3) + 3) % 3, pu = song.pulse(t, 6);
      [0, 1, 2].forEach(i => Zn.doodle('onomatopoeia', 860 + i * 100, CUR_Y - 20 * pu * (i === b ? 1 : 0.15), 22, { seed: 80 + i, color: C.ink, w: 3 }));
    }
  }

  /* ======================================================================
     the scene
     ====================================================================== */
  class Mic extends Scene {
    enter() {
      this.song = null; this.plan = null; this.err = null;
      this.playing = false; this.frozen = false; this.ended = false; this.startedOnce = false; this.startAt = null;
      this.pos = 0; this.t0ctx = null; this.heard = 0; this.lastT = 0;
      this.audioReady = false; this.completed = false; this.gone = false;
      Audio.stopMusic();
      if (!window.Narration) this.err = 'the narrator model is missing';
      const wurl = Assets.url(WORDS);
      const words = (window.Loader ? Loader.json(wurl).then(d => d || Promise.reject(new Error('no timings'))) : fetch(wurl).then(r => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))))
        .then(d => { this.song = new Song(d); this.plan = plan(this.song); })
        .catch(e => { this.err = String(e && e.message); console.warn('[mic] no timings', this.err); });
      const audio = (Audio.has(KEY) ? Promise.resolve() : Audio.load({ [KEY]: Assets.url(AUDIO) })).then(() => { this.audioReady = Audio.has(KEY); });
      Promise.all([words, audio]).then(() => {
        if (this.gone || this.frozen || this.startedOnce || !this.song) return;
        // the mic tap, then the piece
        if (Audio.has('mic_tap') && !(window.__timeScale > 1)) Audio.play('mic_tap', { gain: 0.5 });
        this.startAt = Game.time + (window.__timeScale > 1 ? 0 : START_DELAY);
      });
      if (window.Device) {
        this.onHidden = Device.on('hidden', () => { if (this.playing && !this.gone) { this.pause(); this.resumeOnReturn = true; } });
        this.onResume = Device.on('resume', () => { if (this.resumeOnReturn && !this.gone && Game.current() === this) { this.resumeOnReturn = false; this.play(this.pos); } });
      }
    }
    exit() {
      this.gone = true; Audio.stopGroup('song'); this.playing = false;
      if (this.media) this.media.pause();
      if (window.Device) { Device.off('hidden', this.onHidden); Device.off('resume', this.onResume); }
    }
    get key() { return KEY; }
    /* the song clock: the <audio> element while it plays (t0ctx != null = "on the audio clock"), else pos */
    now() {
      if (this.playing && this.t0ctx != null) {
        const m = this.media;
        if (m) return m.playing() ? m.time() : m.el && m.el.ended ? this.song.dur : this.pos;
        if (Audio.running()) return Audio.now() - this.t0ctx;
      }
      return this.pos;
    }
    play(from = this.now()) {
      if (!this.song) return;
      this.startedOnce = true; this.frozen = false; this.ended = false; this.startAt = null;
      from = clamp(from, 0, this.song.dur - 0.05);
      Audio.stopGroup('song');
      this.pos = from; this.lastT = from; this.playing = true; this.t0ctx = null;
      if (!this.audioReady || window.__timeScale > 1) return;
      this.media = this.media || Audio.media(KEY);
      if (this.media) {
        const m = this.media, tok = this.playTok = (this.playTok || 0) + 1;
        this.playAt = Game.time; this.blocked = false;
        m.play(from).then(ok => {
          if (tok !== this.playTok || this.gone) return;
          if (ok && this.playing) { this.pos = m.time(); this.t0ctx = 0; }
          else if (!ok) { this.pause(); this.blocked = true; }
        });
      } else if (Audio.running()) {
        const at = Audio.now() + 0.06;
        Audio.schedule(KEY, { at, offset: from, gain: 1, group: 'song', bus: 'voice', fade: 0.03 });
        this.t0ctx = at - from;
      }
    }
    pause() {
      this.pos = this.now(); this.playing = false; this.t0ctx = null; this.startAt = null; this.playTok = (this.playTok || 0) + 1;
      Audio.stopGroup('song');
      if (this.media) this.media.pause();
    }
    toggle() { if (this.playing) this.pause(); else this.play(this.ended ? 0 : this.pos); Audio.play('paper', { gain: 0.25 }); }
    seek(t) { if (!this.song) return; const was = this.playing; this.pause(); this.pos = clamp(t, 0, this.song.dur); this.lastT = this.pos; this.ended = false; if (was) this.play(this.pos); }
    restart() { this.heard = 0; this.play(0); }
    /* QA: freeze on song time t (no pause overlay) */
    debugAt(t) { this.pause(); this.frozen = true; this.startedOnce = true; this.ended = false; this.pos = t; this.lastT = t; }
    /* stamp the step now (the end of a ≥ 90 % listen, the "Finish" button, QA) */
    done() { if (this.completed) return; this.completed = true; if (!Game.progress.stamps[STEP]) Game.complete(STEP); }
    finishStub() { this.done(); }
    canFinish() { return !!this.song && !this.completed && this.heard >= THRESH * this.song.dur; }
    lyricAt(t) { return this.song ? lyricAt(this.song, t) : null; }
    activeWord(t) { return this.song ? activeWord(this.song, t) : null; }
    actAt(t) { return this.plan ? this.plan.acts[actAt(this.plan, t)].id : null; }
    tagsAt(t) { return this.plan ? tagsAt(this.song, this.plan, t) : []; }
    marksAt(t) {
      const c = this.song && lyricAt(this.song, t).cur; if (!c) return [];
      return c.marks.filter(m => t >= c.words[m.j0].s - 0.05).map(m => ({ line: c.i, dev: m.dev, colour: COL(m.dev), words: c.words.slice(m.j0, m.j1 + 1).map(w => w.w).join(' ') }))
        .concat(c.strike.filter(q => t >= q.t).map(q => ({ line: c.i, strike: c.words[q.j].w })));
    }

    update(dt) {
      if (this.startAt != null && !this.playing && Game.time >= this.startAt && !this.gone) this.play(0);
      if (!this.song || !this.playing || this.scrub) return;
      if (this.t0ctx == null) {
        this.pos += dt * (window.__timeScale || 1);
        const retry = this.audioReady && !(window.__timeScale > 1) &&
          (this.media ? !this.blocked && Game.time - (this.playAt || 0) > 3 : Audio.running());
        if (retry) this.play(this.pos);
      }
      this.heard += dt * (this.t0ctx == null ? (window.__timeScale || 1) : 1);
      const now = this.now();
      if (this.lastT < this.plan.tStamp && now >= this.plan.tStamp) {
        Audio.play('stamp', { gain: 0.6 });
        if (Audio.has('snaps')) Audio.play('snaps', { gain: 0.55 });
      }
      this.lastT = now;
      if (now >= this.song.dur - 0.05) {
        this.pause(); this.pos = this.song.dur; this.ended = true;
        if (!this.completed && this.heard >= THRESH * this.song.dur) this.done();
      }
    }
    keyDown(k) {
      if (!this.song) return;
      if (k === ' ') this.toggle();
      else if (k === 'r' || k === 'R') this.restart();
      else if (k === 'ArrowRight') this.seek(this.now() + 5);
      else if (k === 'ArrowLeft') this.seek(this.now() - 5);
      else if (k === 'Enter' && this.ended) Game.replace(new Levels.map());
    }
    stripZone() { return Math.max(32, UI.tap(32)); }
    pointerDown(x, y) {
      if (!this.song || this.ended) return;
      if (UI.docked()) { this.toggle(); return; }
      if (UI.inHud(x, y, 3)) return;
      if (this.finishRect && Math.abs(x - this.finishRect[0]) < this.finishRect[2] / 2 && Math.abs(y - this.finishRect[1]) < this.finishRect[3] / 2) return;
      if (y > 1080 - this.stripZone()) {
        this.scrub = { was: this.playing };
        if (this.playing) this.pause();
        this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); this.ended = false;
        return;
      }
      this.toggle();
    }
    pointerMove(x) { if (this.scrub && this.song) this.pos = clamp(x / 1920 * this.song.dur, 0, this.song.dur - 0.05); }
    pointerUp() {
      if (!this.scrub) return;
      const was = this.scrub.was; this.scrub = null; this.lastT = this.pos;
      if (was) this.play(this.pos);
    }

    draw() {
      const docked = UI.docked(), title = 'Open Mic II';
      if (!this.song || this.err) {
        g.fillStyle = paperTile('#2c2724', 51, 'tex_kraft'); g.fillRect(0, 0, 1920, 1080);
        ransom('OPEN MIC II', 960, 400, 110, this.enteredAt + 0.2, { seed: 42, palette: HOOK_PAL });
        const msg = this.err ? 'the piece is missing (' + this.err + ')' : 'tapping the mic…';
        hand(msg, 960, 580, 54, this.enteredAt + 0.4, { color: '#f7f5ef', seed: 44 });
        if (docked) { Dock.head(title, ''); Dock.text('mic-wait', [msg], { cls: 'note center' }); this.dockBar(); }
        else this.chrome(null);
        return;
      }
      const t = clamp(this.now(), 0, this.song.dur), gT = T, gBF = BF;
      T = t; BF = Math.floor(t * 8);
      try {
        drawStage(this.song, this.plan, t);
        if (!docked) band(this.song, t);
      } finally { T = gT; BF = gBF; }
      if (docked) this.dock(t, title); else this.chrome(t);
    }

    /* ---------- the portrait dock: karaoke, the tag note, controls and the strip below the stage ---------- */
    dock(t, title) {
      const S = this.song, pl = this.plan;
      const mmss = s => Math.floor(s / 60) + ':' + String(Math.floor(s % 60)).padStart(2, '0');
      Dock.head(title, `${mmss(t)} / ${mmss(S.dur)}`);
      const { cur, next } = lyricAt(S, t);
      const sec = (cur || next || {}).section;
      const lastE = S.items.length ? S.items[S.items.length - 1].e : 0;
      if (cur) {
        const key = cur.i + ':' + cur.s;
        if (!this._dk || this._dk.key !== key) this._dk = { key, words: cur.words.map(w => ({ w: w.w, s: w.s, e: w.e, hook: !!cur.hook })) };
        Dock.karaoke({ key, words: this._dk.words, t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '' });
      } else Dock.karaoke({ key: 'gap', words: [], t, next: next ? next.text : '', section: sec ? sec.toLowerCase() : '',
        note: t > lastE && S.items.length ? 'Who\'s telling this? Ask before you trust.' : this.startedOnce || this.frozen ? '♪ ♫ ♪' : 'tapping the mic…' });
      const dn = tagNote(cur, t);
      Dock.text('mic-dev', [dn ? dn.text : ' '], { cls: 'small center' });
      Dock.text('credit', ['♪ ' + CREDIT], { cls: 'small center' });
      if (this.ended && Game.current() === this) {
        Dock.buttons('mic-end', [{ label: '↺ Play it again', color: PAL.teal, onClick: () => this.restart() },
          { label: 'Back to the map', color: PARK, onClick: () => Game.replace(new Levels.map()) }]);
      } else if (this.canFinish() && Game.current() === this) {
        Dock.buttons('mic-finish', [{ label: 'Finish', color: ZC().red, onClick: () => this.done() }]);
      }
      Dock.strip({ value: t, total: S.dur, ticks: pl.ticks, label: 'position in the piece', onSeek: (v, phase) => this.scrubTo(v, phase) });
      this.dockBar();
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended && !this.scrub) this.pausedMark(540, 150, 1080);
    }
    dockBar() {
      if (this.song && !this.ended) {
        Dock.button({ id: 'restart', glyph: '↺', label: 'restart', aria: 'play the piece from the start', onClick: () => this.restart() });
        Dock.button({ id: 'play', glyph: this.playing ? '❚❚' : '▶', label: this.playing ? 'pause' : 'play', aria: this.playing ? 'pause' : 'play', primary: true, onClick: () => this.toggle() });
      }
      Dock.button({ id: 'map', glyph: '⌂', label: 'map', aria: 'back to the map', onClick: () => Game.replace(new Levels.map()) });
    }
    scrubTo(v, phase) {
      if (!this.song) return;
      const pos = clamp(v, 0, this.song.dur - 0.05);
      if (phase === 'start') { this.scrub = { was: this.playing || (this.scrub && this.scrub.was) }; if (this.playing) this.pause(); this.ended = false; }
      this.pos = pos; this.lastT = pos;
      if (phase === 'end') { const was = this.scrub ? this.scrub.was : false; this.scrub = null; if (was) this.play(this.pos); }
    }
    /* the paused play mark: a dim wash and a highlighter-yellow paper disc with an ink triangle */
    pausedMark(y, r, h) {
      g.save(); g.fillStyle = 'rgba(20,18,16,0.35)'; g.fillRect(0, 0, 1920, h); g.restore();
      paperCircle(960, y, r, '#fff04a', { seed: 63, depth: 1.6 });
      g.save(); g.fillStyle = '#141210'; g.beginPath(); g.moveTo(960 - r * 0.32, y - r * 0.55); g.lineTo(960 + r * 0.55, y); g.lineTo(960 - r * 0.32, y + r * 0.55); g.closePath(); g.fill(); g.restore();
    }

    chrome(t) {
      const t0 = this.enteredAt, S = this.song, C = ZC();
      ransom('OPEN MIC II', 40, 56, 36, t0 + 0.1, { align: 'left', seed: 61, palette: HOOK_PAL });
      hand('♪ ' + CREDIT, 44, 114, 28, t0 + 0.4, { align: 'left', color: '#f7f5ef', seed: 62, dur: 0.4 });
      const m = UI.hudSlot(0), r = UI.hudSlot(1), pl = UI.hudSlot(2), fx = { r: m.r, fixed: true };
      if (UI.iconButton('mic-map', '⌂', m.x, m.y, fx)) { Game.replace(new Levels.map()); return; }
      if (S) {
        if (UI.iconButton('mic-restart', '↺', r.x, r.y, fx)) this.restart();
        if (UI.iconButton('mic-play', this.playing ? '❚❚' : '▶', pl.x, pl.y, { ...fx, color: '#fff04a' })) this.toggle();
      }
      if (t == null) return;
      const zone = this.stripZone(), touch = zone > 32;
      if (!this.ended) UI.target('mic-strip', 960, 1080 - zone / 2, 1920, zone);
      const sh = touch ? 22 : 12, sy = 1080 - sh;
      g.save();
      g.fillStyle = 'rgba(20,18,16,0.25)'; g.fillRect(0, sy, 1920, sh);
      g.fillStyle = C.red; g.fillRect(0, sy, 1920 * t / S.dur, sh);
      g.fillStyle = C.ink; this.plan.ticks.forEach(b => g.fillRect(1920 * b / S.dur - 2, sy - 6, 4, sh + 6));
      g.restore();
      if (touch || this.scrub) paperCircle(clamp(1920 * t / S.dur, 26, 1894), touch ? 1080 - 24 : sy + sh / 2, touch ? 22 : 14, C.ink, { seed: 66, depth: 0.8, boil: false });
      if (!this.playing && !this.frozen && this.startedOnce && !this.ended) {
        this.pausedMark(430, 100, BAND_Y);
        hand(UI.hint('paused · click or Space to play', 'paused · tap to play'), 960, 590, 48, -1e6, { dur: 0.01, color: '#f7f5ef', outline: 'rgba(20,18,16,0.92)', seed: 64 });
      }
      if (!this.startedOnce && !this.frozen) hand('tapping the mic…', 960, 590, 48, this.enteredAt + 0.3, { color: '#f7f5ef', seed: 65 });
      this.finishRect = null;
      if (this.ended && Game.current() === this) {
        if (UI.button('mic-again', '↺ Play it again', 700, 640, { w: 440, h: 110, color: PAL.teal, size: 56 })) this.restart();
        if (UI.button('mic-back', 'Back to the map', 1220, 640, { w: 440, h: 110, color: PARK, size: 56 })) Game.replace(new Levels.map());
      } else if (this.canFinish() && Game.current() === this) {
        const bw = 300, bh = UI.tap(90), bx = 1920 - 40 - bw / 2, by = m.y + m.r + 28 + bh / 2;
        this.finishRect = [bx, by, bw, bh];
        if (UI.button('mic-finish', 'Finish', bx, by, { w: bw, h: 90, color: C.red, size: 52 })) this.done();
      }
    }
  }
  Mic.STEP = STEP;
  Mic.TAKE = TAKE_NAME;
  Mic.Song = Song;
  Mic.plan = plan;
  Mic.MARKS = MARKS;
  Mic.isHook = isHook;
  Mic.clueOf = clueOf;
  Levels.mic = Mic;
})();
